/**
 * Voice Input Button Component
 * Professional microphone button with visual feedback and state management
 * 
 * @author Software Engineering Team
 * @version 1.0.0
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  StopCircle as StopIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

import VoiceSTTService from '../services/VoiceSTTService';
import './VoiceInputButton.css';

const VoiceInputButton = ({ 
  onTranscript, 
  onInterim,
  onError, 
  disabled = false,
  size = 'medium',
  className = ''
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  
  const voiceServiceRef = useRef(null);
  const timeoutRef = useRef(null);

  /**
   * Initialize voice service
   */
  const initializeVoiceService = useCallback(async () => {
    if (voiceServiceRef.current) return voiceServiceRef.current;

    try {
      setIsInitializing(true);
      const service = new VoiceSTTService();
      
      // Set up callbacks
      service.setCallbacks({
        onStart: handleRecordingStart,
        onResult: handleRecordingResult,
        onEnd: handleRecordingEnd,
        onError: handleRecordingError,
        onInterim: handleInterimResult
      });

      const initialized = await service.initialize();
      if (!initialized) {
        throw new Error('Failed to initialize voice service');
      }

      voiceServiceRef.current = service;
      return service;
      
    } catch (error) {
      console.error('Voice service initialization failed:', error);
      handleInitializationError(error);
      return null;
    } finally {
      setIsInitializing(false);
    }
  }, []);

  /**
   * Handle recording start
   */
  const handleRecordingStart = useCallback(() => {
    setIsRecording(true);
    setInterimTranscript('');
    setError(null);
    console.info('Voice recording started');
  }, []);

  /**
   * Handle final recognition result
   */
  const handleRecordingResult = useCallback((result) => {
    console.info('Voice result received:', result);
    
    // Always use the result - no confirmation needed
    if (result.transcript && result.transcript.trim().length > 0) {
      onTranscript?.(result.transcript);
    }
    
    setInterimTranscript('');
  }, [onTranscript]);

  /**
   * Handle interim (partial) results
   */
  const handleInterimResult = useCallback((result) => {
    setInterimTranscript(result.transcript);
    // Send interim results to parent for real-time typing
    onInterim?.(result.transcript);
  }, [onInterim]);

  /**
   * Handle recording end
   */
  const handleRecordingEnd = useCallback(() => {
    setIsRecording(false);
    setInterimTranscript('');
    console.info('Voice recording ended');
  }, []);

  /**
   * Handle recording errors
   */
  const handleRecordingError = useCallback((error) => {
    console.error('Voice recording error:', error);
    setIsRecording(false);
    setInterimTranscript('');
    setError(error);
    setShowError(true);
    onError?.(error);
  }, [onError]);

  /**
   * Handle initialization errors
   */
  const handleInitializationError = useCallback((error) => {
    const userError = {
      type: 'INITIALIZATION_ERROR',
      userMessage: 'Voice input is not available in your browser or requires microphone permission.',
      suggestions: [
        'Make sure you\'re using Chrome, Edge, or Safari',
        'Allow microphone access when prompted',
        'Check that your microphone is connected'
      ]
    };
    setError(userError);
    setShowError(true);
    onError?.(userError);
  }, [onError]);

  /**
   * Start voice recording
   */
  const startRecording = useCallback(async () => {
    try {
      if (disabled || isRecording || isInitializing) return;

      const service = await initializeVoiceService();
      if (!service) return;

      await service.startRecording();
      
      // Set timeout as fallback
      timeoutRef.current = setTimeout(() => {
        if (isRecording) {
          stopRecording();
        }
      }, 30000); // 30 second timeout
      
    } catch (error) {
      console.error('Failed to start recording:', error);
      handleRecordingError(error);
    }
  }, [disabled, isRecording, isInitializing, initializeVoiceService]);

  /**
   * Stop voice recording
   */
  const stopRecording = useCallback(() => {
    if (voiceServiceRef.current && isRecording) {
      voiceServiceRef.current.stopRecording();
    }
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [isRecording]);

  /**
   * Handle button click
   */
  const handleButtonClick = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);


  /**
   * Close error dialog
   */
  const handleCloseError = useCallback(() => {
    setShowError(false);
    setError(null);
  }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (voiceServiceRef.current) {
        voiceServiceRef.current.destroy();
        voiceServiceRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  /**
   * Get button icon based on state
   */
  const getButtonIcon = () => {
    if (isInitializing) {
      return <CircularProgress size={20} color="inherit" />;
    }
    if (isRecording) {
      return <StopIcon />;
    }
    if (error && error.category === 'permission') {
      return <MicOffIcon />;
    }
    return <MicIcon />;
  };

  /**
   * Get button color based on state
   */
  const getButtonColor = () => {
    if (isRecording) return 'secondary';
    if (error) return 'error';
    return 'primary';
  };

  /**
   * Get tooltip text
   */
  const getTooltipText = () => {
    if (disabled) return 'Voice input disabled';
    if (isInitializing) return 'Initializing voice recognition...';
    if (isRecording) return 'Click to stop recording';
    if (error) return 'Voice input unavailable - click for details';
    return 'Click and speak to input text';
  };

  return (
    <>
      <Tooltip title={getTooltipText()} arrow>
        <span>
          <IconButton
            onClick={handleButtonClick}
            disabled={disabled || isInitializing}
            color={getButtonColor()}
            size={size}
            className={`voice-input-button ${isRecording ? 'recording' : ''} ${className}`}
            aria-label={isRecording ? 'Stop recording' : 'Start voice input'}
          >
            {getButtonIcon()}
          </IconButton>
        </span>
      </Tooltip>

      {/* Interim transcript display */}
      {interimTranscript && (
        <div className="interim-transcript">
          <Typography variant="caption" color="textSecondary">
            Listening: {interimTranscript}
          </Typography>
        </div>
      )}


      {/* Error Dialog */}
      <Dialog
        open={showError}
        onClose={handleCloseError}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <WarningIcon color="error" />
            Voice Input Issue
          </div>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error?.userMessage || 'An error occurred with voice input.'}
          </Alert>
          
          {error?.suggestions && error.suggestions.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Suggestions:
              </Typography>
              <List dense>
                {error.suggestions.map((suggestion, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                      primary={suggestion}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          <div style={{ marginTop: 16 }}>
            <Chip 
              label={`Error Type: ${error?.type || 'Unknown'}`}
              size="small"
              variant="outlined"
              color="error"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseError}>
            Close
          </Button>
          {error?.isRetryable && (
            <Button 
              onClick={() => {
                handleCloseError();
                startRecording();
              }} 
              variant="contained"
            >
              Try Again
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VoiceInputButton;
